/**
 * In-flight request deduplication + short TTL cache.
 * Same key returns the same promise while a request is in flight;
 * after resolve, result is cached for ttlMs so immediate duplicate calls skip the network.
 * @param {string} key - Cache key (e.g. userId, "features:storeId").
 * @param {() => Promise<any>} requestFn - Function that performs the request (no args).
 * @param {number} [ttlMs=2000] - How long to cache a successful result (ms).
 * @returns {Promise<any>}
 */
export function dedupedRequest(key, requestFn, ttlMs = 2000) {
  const inFlight = new Map();
  const cache = new Map();
  const expiry = new Map();

  return function run() {
    const now = Date.now();
    const cached = cache.get(key);
    const exp = expiry.get(key);
    if (cached !== undefined && exp != null && now < exp) {
      return Promise.resolve(cached);
    }
    const pending = inFlight.get(key);
    if (pending) {
      return pending;
    }
    const promise = requestFn()
      .then((result) => {
        inFlight.delete(key);
        cache.set(key, result);
        expiry.set(key, now + ttlMs);
        setTimeout(() => {
          cache.delete(key);
          expiry.delete(key);
        }, ttlMs);
        return result;
      })
      .catch((err) => {
        inFlight.delete(key);
        throw err;
      });
    inFlight.set(key, promise);
    return promise;
  };
}

/**
 * Create a deduped version of an API call that takes params and derives a key.
 * @param {(params: T) => string} keyFn - (params) => cache key
 * @param {(params: T) => Promise<any>} requestFn - (params) => request
 * @param {number} [ttlMs=2000]
 * @returns {(params: T) => Promise<any>}
 */
export function createDedupedRequest(keyFn, requestFn, ttlMs = 2000) {
  const inFlight = new Map();
  const cache = new Map();
  const expiry = new Map();

  return function deduped(params) {
    const key = keyFn(params);
    const now = Date.now();
    const cached = cache.get(key);
    const exp = expiry.get(key);
    if (cached !== undefined && exp != null && now < exp) {
      return Promise.resolve(cached);
    }
    const pending = inFlight.get(key);
    if (pending) {
      return pending;
    }
    const promise = requestFn(params)
      .then((result) => {
        inFlight.delete(key);
        cache.set(key, result);
        expiry.set(key, now + ttlMs);
        setTimeout(() => {
          cache.delete(key);
          expiry.delete(key);
        }, ttlMs);
        return result;
      })
      .catch((err) => {
        inFlight.delete(key);
        throw err;
      });
    inFlight.set(key, promise);
    return promise;
  };
}
